import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { buildingHomeContentApi } from '../../api/buildingHomeContentApi';
import { ImageUploadField } from '../../components/common/ImageUploadField';
import { RichTextEditor } from '../../components/common/RichTextEditor';
import { combinePublishedAt, splitPublishedAt } from '../../utils/newsDateTime';

export const AddEditNews: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    buildingId: user?.buildingId || '',
    title: '',
    summary: '',
    content: '',
    coverImage: '',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED',
    isPinned: false,
    publishedAt: '',
    publishedTime: '09:00',
    displayOrder: 0,
  });

  useEffect(() => {
    loadBuildings();
    if (isEdit && id) {
      loadArticle(id);
    }
  }, [id]);

  const loadBuildings = async () => {
    const response = await buildingsApi.getBuildings();
    if (Array.isArray(response)) {
      setBuildings(response);
      if (!isEdit && !formData.buildingId) {
        const defaultId = user?.buildingId || response[0]?.id || '';
        if (defaultId) {
          setFormData((prev) => ({ ...prev, buildingId: defaultId }));
        }
      }
    }
  };

  const loadArticle = async (articleId: string) => {
    try {
      const article = await buildingHomeContentApi.getNewsArticle(articleId);
      const { date, time } = splitPublishedAt(article.publishedAt);
      setFormData({
        buildingId: article.buildingId,
        title: article.title,
        summary: article.summary || '',
        content: article.content || '',
        coverImage: article.coverImage || '',
        status: article.status,
        isPinned: article.isPinned,
        publishedAt: date,
        publishedTime: time,
        displayOrder: article.displayOrder,
      });
    } catch {
      alert('Không thể tải bài viết');
      navigate('/building-news');
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!formData.buildingId) {
      alert('Vui lòng chọn tòa nhà trước khi upload ảnh');
      return;
    }

    try {
      setUploading(true);
      const result = await buildingHomeContentApi.uploadImage(file, formData.buildingId, 'news');
      setFormData((prev) => ({ ...prev, coverImage: result.url }));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Upload ảnh thất bại');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.buildingId || !formData.title.trim()) {
      alert('Vui lòng nhập tiêu đề và chọn tòa nhà');
      return;
    }

    try {
      setLoading(true);
      const publishedAtValue = combinePublishedAt(formData.publishedAt, formData.publishedTime);
      const payload = {
        title: formData.title.trim(),
        summary: formData.summary.trim(),
        content: formData.content.trim(),
        coverImage: formData.coverImage.trim(),
        status: formData.status,
        isPinned: formData.isPinned,
        publishedAt: publishedAtValue,
        displayOrder: Number(formData.displayOrder) || 0,
      };

      if (isEdit && id) {
        await buildingHomeContentApi.updateNewsArticle(id, payload);
      } else {
        await buildingHomeContentApi.createNewsArticle({
          buildingId: formData.buildingId,
          title: payload.title,
          summary: payload.summary || undefined,
          content: payload.content || undefined,
          coverImage: payload.coverImage || undefined,
          status: payload.status,
          isPinned: payload.isPinned,
          publishedAt: publishedAtValue || undefined,
          displayOrder: payload.displayOrder,
        });
      }
      navigate('/building-news');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Lưu bài viết thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/building-news" className="hover:text-primary-600">Quản lý tin tức</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{isEdit ? 'Sửa tin tức' : 'Thêm tin tức'}</span>
      </nav>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEdit ? 'Sửa tin tức' : 'Thêm tin tức'}</h1>
      <p className="text-gray-600 mb-8">Tin tức hiển thị trên trang chủ ứng dụng mobile</p>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tòa nhà</label>
          <select
            value={formData.buildingId}
            onChange={(e) => setFormData((prev) => ({ ...prev, buildingId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
            disabled={isEdit}
          >
            <option value="">-- Chọn tòa nhà --</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tóm tắt</label>
          <textarea
            value={formData.summary}
            onChange={(e) => setFormData((prev) => ({ ...prev, summary: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nội dung</label>
          <RichTextEditor
            value={formData.content}
            onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
            placeholder="Nhập nội dung bài viết..."
            height={300}
          />
          <div className="text-xs text-gray-500 mt-2">
            Mẹo: Dùng nút bảng trong thanh công cụ để tạo bảng chuyên nghiệp. Hỗ trợ copy-paste từ Word/Excel.
          </div>
        </div>

        <ImageUploadField
          label="Ảnh bìa"
          imageUrl={formData.coverImage}
          uploading={uploading}
          disabled={!formData.buildingId}
          onUpload={handleImageUpload}
          hint={!formData.buildingId ? 'Chọn tòa nhà trước khi upload ảnh' : 'JPG, PNG, WEBP. Tối đa 10MB'}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as typeof formData.status }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="DRAFT">Nháp</option>
              <option value="PUBLISHED">Đã xuất bản</option>
              <option value="ARCHIVED">Lưu trữ</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày xuất bản</label>
            <input
              type="date"
              value={formData.publishedAt}
              onChange={(e) => setFormData((prev) => ({ ...prev, publishedAt: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Giờ xuất bản</label>
            <input
              type="time"
              value={formData.publishedTime}
              onChange={(e) => setFormData((prev) => ({ ...prev, publishedTime: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">Hiển thị trên ứng dụng mobile cùng ngày xuất bản</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Thứ tự hiển thị</label>
            <input
              type="number"
              min={0}
              value={formData.displayOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center pt-6">
            <input
              type="checkbox"
              id="isPinned"
              checked={formData.isPinned}
              onChange={(e) => setFormData((prev) => ({ ...prev, isPinned: e.target.checked }))}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="isPinned" className="ml-2 text-sm text-gray-700">Ghim bài viết</label>
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-md disabled:opacity-50"
          >
            {loading ? 'Đang lưu...' : 'Lưu'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/building-news')}
            className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Hủy
          </button>
        </div>
      </form>
    </div>
  );
};
